import { useCallback, useState } from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { useRef } from "react";
import { VideoStep } from "../VideoStep/VideoStep";

import "./BynderWithSteps.scss";

export const BynderWithSteps = ({
  steps,
  bynderUrl,
  dictionary,
  showVideo,
  showStepsMobile,
}) => {
  const videoRef = useRef(null);
  const stepContainer = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const stepsRef = useRef([]);

  const checkSteps = useCallback(
    (currentTimeInSeconds) => {
      if (steps && steps.length > 0) {
        let found = false;
        let index = 0;

        do {
          const step = steps[index];
          const stepTimeInSeconds = step.timestamp.minutes * 60 + step.timestamp.seconds;

          if (steps.length - 1 !== index) {
            if (stepTimeInSeconds > currentTimeInSeconds) {
              const newStep = index - 1 >= 0 ? index - 1 : 0;
              if (currentStep !== newStep) {
                setCurrentStep(newStep);
              }
              found = true;
            }
          } else {
            if (stepTimeInSeconds > currentTimeInSeconds) {
              const newStep = index - 1 >= 0 ? index - 1 : 0;
              if (currentStep !== newStep) {
                setCurrentStep(newStep);
              }
            } else if (stepTimeInSeconds <= currentTimeInSeconds) {
              if (currentStep !== index) {
                setCurrentStep(index);
              }
            }
            found = true;
          }

          index++;
        } while (!found);
      }
    },
    [steps, currentStep]
  );

  const onTimeUpdateHandler = useCallback(() => {
    if (videoRef.current && !videoRef.current.paused) {
      const currentTime = videoRef.current.currentTime;
      checkSteps(currentTime);
    }
  }, [checkSteps]);

  const onPlayHandler = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const onPauseHandler = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const onVideoLoadedHandler = useCallback(() => {
    setIsVideoLoaded(true);
    setVideoError(null);
  }, []);

  const onVideoErrorHandler = useCallback((error) => {
    setVideoError("Video kon niet geladen worden");
    console.error("Bynder video error:", error);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep + 1 < steps.length) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      seekToStep(newStep);
      scrollToCurrent(newStep);
    }
  }, [currentStep, steps]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      seekToStep(newStep);
      scrollToCurrent(newStep);
    }
  }, [currentStep]);

  const scrollToCurrent = useCallback((stepIndex = currentStep) => {
    if (steps && stepsRef.current && stepsRef.current[stepIndex]) {
      stepsRef.current[stepIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }, [steps, currentStep]);

  const seekToStep = useCallback(
    (stepIndex) => {
      if (videoRef.current && steps && steps[stepIndex]) {
        const seconds = steps[stepIndex].timestamp.seconds + steps[stepIndex].timestamp.minutes * 60;
        videoRef.current.currentTime = seconds;
        
        if (videoRef.current.paused) {
          videoRef.current.play().catch((error) => {
            console.error("Video play error:", error);
          });
        }
      }
    },
    [steps]
  );
  useEffect(() => {
    scrollToCurrent();
  }, [currentStep, scrollToCurrent]);

  const stepsComponents = useMemo(() => {
    if (steps && steps.length > 0) {
      return steps.map((step, index) => (
        <VideoStep
          key={index}
          dictionary={dictionary}
          step={step}
          ref={(element) => (stepsRef.current[index] = element)}
          isActive={currentStep === index}
        />
      ));
    }
    return [];
  }, [currentStep, steps, dictionary]);
  if (!bynderUrl) {
    return (
      <div className="c-bynder">
        <div className="c-bynder__video__error">
          {dictionary.noVideoAvailable}
        </div>
      </div>
    );
  }

  return (
    <div className="c-bynder">
      <div className="c-bynder__content">
        <div className="c-bynder__video">
          {showVideo ? (            <>
              {videoError && (
                <div className="c-bynder__video__error">
                  {videoError}
                </div>
              )}              <video
                ref={videoRef}
                width="100%"
                controls
                preload="metadata"
                onTimeUpdate={onTimeUpdateHandler}
                onPlay={onPlayHandler}
                onPause={onPauseHandler}
                onLoadedMetadata={onVideoLoadedHandler}
                onError={onVideoErrorHandler}
                onCanPlay={() => setIsVideoLoaded(true)}
              >
                <source src={bynderUrl} type="video/mp4" />
                {dictionary.videoNotSupported}
              </video>
            </>          ) : (
            <div className="c-bynder__video__placeholder">
              <p>{dictionary.videoIsLoading}</p>
            </div>
          )}
        </div>

        {steps && steps.length > 0 && (
          <div
            className={`c-bynder__steps ${
              !showStepsMobile ? "hidden-mobile" : ""
            }`}
          >
            <div className="c-bynder__steps__header">              <h4>{dictionary.stappen}</h4>
              <div className="c-bynder__steps__header-steps-buttons">
                <button
                  disabled={!showVideo || !isVideoLoaded}
                  className="c-senioren__button c-senioren__button--secondary c-senioren__button--icon-only"
                  onClick={prevStep}
                  aria-label="Vorige stap"
                >
                  <i className="fas fa-chevron-up"></i>
                </button>
                {`  ${currentStep + 1}/${steps.length}  `}
                <button
                  disabled={!showVideo || !isVideoLoaded}
                  className="c-senioren__button c-senioren__button--secondary c-senioren__button--icon-only"
                  onClick={nextStep}
                  aria-label="Volgende stap"
                >
                  <i className="fas fa-chevron-down"></i>                </button>
              </div>
            </div>
            <div className="c-bynder__steps__content" ref={stepContainer}>
              {stepsComponents}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};